import React, { useCallback, memo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import type { MonthlyShiftSlotSummary } from '../../../../domain/monthlyShiftSlot';
import { SHIFT_SLOT_STATUS_LABELS } from '../../../../domain/dailyShiftSlot';
import { formatYearMonthToSlash, formatRateAsPercent } from '../../../formatters';

/**
 * MonthlyShiftSlotDeleteDialog のProps型
 *
 * @property {boolean} open - ダイアログの開閉状態
 * @property {MonthlyShiftSlotSummary|null} summary - 削除対象のサマリー（nullの場合は表示しない）
 * @property {function} onClose - ダイアログを閉じるコールバック
 * @property {function} onConfirm - 削除確認コールバック（成功時true、失敗時falseを返す）
 * @property {boolean} isLoading - 削除処理中フラグ
 */
interface Props {
  open: boolean;
  summary: MonthlyShiftSlotSummary | null;
  onClose: () => void;
  onConfirm: (id: string) => Promise<boolean>;
  isLoading: boolean;
}

/**
 * 月次シフト枠削除確認ダイアログコンポーネント
 *
 * @description
 * 月次シフト枠の削除前に確認を行うダイアログ。
 * 削除対象の詳細情報（年月、ステータス、提出率、未確定枠）を表示し、
 * ユーザーに削除の最終確認を求める。
 * 削除処理中はボタンを無効化し、ローディング表示を行う。
 *
 * @param {Props} props - コンポーネントProps
 * @returns {React.ReactElement|null} ダイアログコンポーネント（summaryがnullの場合はnull）
 */
const MonthlyShiftSlotDeleteDialog: React.FC<Props> = memo(({
  open,
  summary,
  onClose,
  onConfirm,
  isLoading
}) => {
  const handleClose = useCallback(() => {
    if (!isLoading) {
      onClose();
    }
  }, [isLoading, onClose]);

  const handleConfirm = useCallback(async () => {
    if (!summary) return;
    await onConfirm(summary.id);
  }, [summary, onConfirm]);

  if (!summary) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="error" />
          <Typography variant="h6">
            月次シフト枠を削除
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          以下の月次シフト枠を削除してもよろしいですか？
        </Typography>

        <Box
          sx={{
            p: 2,
            bgcolor: 'grey.100',
            borderRadius: 1
          }}
        >
          <Typography variant="subtitle2" fontWeight={500}>
            {formatYearMonthToSlash(summary.year, summary.month)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ステータス: {SHIFT_SLOT_STATUS_LABELS[summary.status]}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            提出率: {formatRateAsPercent(summary.submissionRate)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            未確定枠: {summary.unconfirmedSlots}
          </Typography>
        </Box>

        <Typography
          variant="body2"
          color="error"
          sx={{ mt: 2 }}
        >
          ※ この操作は取り消せません
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={isLoading}
        >
          キャンセル
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          {isLoading ? '削除中...' : '削除'}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

MonthlyShiftSlotDeleteDialog.displayName = 'MonthlyShiftSlotDeleteDialog';

export default MonthlyShiftSlotDeleteDialog;
